import { useCallback } from 'react';
import { Editor, Node, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { focusEditor, PROMPT_MENU_TYPE, selectWholeNode } from 'src/components/RichSlate';
import { getPromptAncestorNode } from 'src/components/RichSlate/utils';
import { captureMessage } from 'src/lib/sentry';
import { SLATE_NODE_TYPES, SLATE_TEXT_FORMATS } from 'src/lib/slate';

export const useHandlers = ({
  editor,
  onMenuOpen,
  onCloseMenu,
  setMenuType,
  noTags,
  value,
  onQuickSearchTriggered,
  trackingEvents,
  setPlusIconClicked,
  onMenuButtonClick,
  setWasOpened,
  onEditorFocus,
  setPlusButtonRef,
  editorRef,
  autoShowMenu,
}) => {
  const openMenuAtPoint = useCallback(
    ({ x, y }) => {
      try {
        //prepare mock element to be used as an anchor for the menu
        const target = document.createElement('div');
        target.setAttribute('class', 'tempMenuAnchor');
        target.setAttribute(
          'style',
          `position:absolute;
          left: ${x}px;
          top: ${y}px;`
        );
        document.body.appendChild(target);

        const menuProps = {
          anchorOrigin: {
            vertical: 'top',
          },
          transformOrigin: {
            vertical: 'top',
          },
        };

        onMenuOpen(target, menuProps);
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'InstructionsTextarea/useHandlers -> openMenuAtPoint',
          },
        });
      }
    },
    [onMenuOpen]
  );

  const handleFocus = useCallback(() => {
    if (!(value === '<p></p>')) {
      return;
    }
    const selection = {
      anchor: {
        path: [0, 0],
        offset: 0,
      },
      focus: {
        path: [0, 0],
        offset: 0,
      },
    };
    const range = ReactEditor.toDOMRange(editor, selection);
    const { x, bottom } = range.getBoundingClientRect();

    openMenuAtPoint({ x: x - 6, y: bottom });
  }, [value, editor, openMenuAtPoint]);

  const handlePaste = useCallback(
    (event) => {
      try {
        event.preventDefault();

        const text = event.clipboardData.getData('text/plain');

        // Use a regular expression to find text inside square brackets
        const regex = /(\[.*?\]|[^[]+)/g;
        const matches = [...text.matchAll(regex)];

        // Process the matches and insert them into the editor
        matches.forEach((match, index) => {
          const { selection } = editor;
          const textContent = match[0];

          // Check if the text contains brackets
          if (!noTags && textContent.startsWith('[') && textContent.endsWith(']')) {
            const newElement = {
              type: SLATE_TEXT_FORMATS.PROMPT_TAG,
              children: [{ text: `${textContent}` }],
            };

            editor.insertNode(newElement, { at: selection || null });
            if (editor.selection) {
              const newSelection = Editor.after(editor, editor.selection.focus.path);
              Transforms.select(editor, newSelection);
            }
          } else {
            const ancestorNode = getPromptAncestorNode(editor);
            if (ancestorNode?.path) {
              Transforms.removeNodes(editor, { at: ancestorNode.path });
            }
            Transforms.insertText(editor, textContent, { at: editor.selection || null });
          }
        });
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'InstructionsTextarea/useHandlers -> handlePaste',
          },
        });
      }
    },
    [editor, noTags]
  );

  const handleClosingBracket = useCallback(() => {
    try {
      const { selection } = editor;

      if (selection) {
        const [currentNode] = Editor.node(editor, selection);

        if (currentNode) {
          const nodeText = Node.string(currentNode);
          const match = nodeText.match(/\[(.*?)]/); // Find text between brackets

          if (!noTags && match) {
            const [, textInsideBrackets] = match;
            const newElement = {
              type: SLATE_TEXT_FORMATS.PROMPT_TAG,
              children: [{ text: textInsideBrackets }],
            };

            const start = match.index;
            const end = start + textInsideBrackets.length + 2;

            const range = {
              anchor: { path: editor.selection.anchor.path, offset: start },
              focus: { path: editor.selection.anchor.path, offset: end },
            };

            Transforms.wrapNodes(editor, newElement, { split: true, at: range });
            Transforms.collapse(editor, { edge: 'end' });
            Transforms.insertFragment(editor, [
              { type: SLATE_NODE_TYPES.INLINE, children: [{ text: ' ' }] },
            ]);
          }
        }
      }
    } catch (error) {
      captureMessage('Prompt field issue', {
        level: 'warning',
        extra: {
          error: error.toString(),
          source: 'InstructionsTextarea/useHandlers -> handleClosingBracket',
        },
      });
    }
  }, [editor, noTags]);

  const handleKeyUp = useCallback(
    ({ key }) => {
      try {
        const { selection } = editor;

        if (['/', '@'].includes(key)) {
          onCloseMenu();
        }

        if (!selection) {
          return;
        }

        const range = ReactEditor.toDOMRange(editor, selection);
        const { x, bottom } = range.getBoundingClientRect();

        if (key === '/') {
          setMenuType(PROMPT_MENU_TYPE.default);
          openMenuAtPoint({ x: x - 6, y: bottom });
        } else if (key === '@') {
          setMenuType(PROMPT_MENU_TYPE.all);
          onQuickSearchTriggered();
          trackingEvents?.trackResourceSearchDisplayed('keyboard');
          openMenuAtPoint({ x: x - 6, y: bottom });
        } else if (key === ']') {
          handleClosingBracket();
        }
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'InstructionsTextarea/useHandlers -> handleKeyUp',
          },
        });
      }
    },
    [
      editor,
      handleClosingBracket,
      openMenuAtPoint,
      onCloseMenu,
      setMenuType,
      onQuickSearchTriggered,
      trackingEvents,
    ]
  );

  const handleOnClick = useCallback(
    (event) => {
      try {
        event.stopPropagation();
        let slateNode;
        let el = event.target;
        while (el) {
          // on rare cases the wrapper element is caught, let's traverse the elements and look for the correct one
          try {
            slateNode = ReactEditor.toSlateNode(editor, el);
            break;
          } catch {
            el = el.firstChild;
          }
        }

        let path = ReactEditor.findPath(editor, slateNode);
        let node = Node.get(editor, path);

        if (path?.length > 0) {
          // in case a child text node got the click
          while (!node.type) {
            path = Path.parent(path);
            node = Node.get(editor, path);
          }
        }

        if (
          [
            SLATE_TEXT_FORMATS.BANK_MESSAGE,
            SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
            SLATE_TEXT_FORMATS.PROMPT_TAG,
            SLATE_TEXT_FORMATS.RESOURCE_URL,
            SLATE_TEXT_FORMATS.PROMPT_IMAGE,
            SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
          ].includes(node?.type)
        ) {
          selectWholeNode(editor, node);
        }
      } catch (error) {
        captureMessage('Prompt field issue', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'InstructionsTextarea/useHandlers -> handleOnClick',
          },
        });
      }
    },
    [editor]
  );

  const handleAddClick = useCallback(
    (event) => {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuType(PROMPT_MENU_TYPE.default);
      setPlusIconClicked(true);
      onMenuButtonClick(event, rect, { left: 0, top: -2 });
      setWasOpened(true);
      trackingEvents?.trackPlusIconClicked?.();
    },
    [setMenuType, setPlusIconClicked, onMenuButtonClick, setWasOpened, trackingEvents]
  );

  const handleTextAreaClick = useCallback(
    (event) => {
      onEditorFocus?.();
      handleOnClick(event);
      setPlusButtonRef?.(editorRef?.current);
      if (autoShowMenu) {
        handleFocus();
      }
      focusEditor({ editor, atEnd: true });
    },
    [onEditorFocus, handleOnClick, setPlusButtonRef, editorRef, autoShowMenu, handleFocus, editor]
  );

  return {
    handleKeyUp,
    handlePaste,
    handleAddClick,
    handleTextAreaClick,
  };
};
